import React, { useCallback, useMemo, useState } from 'react';
import { Table, SearchBar, Tag } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import type {
  AdminUserPermissions,
  AdminUserSearchField,
  AdminUserSearchFormState,
  AdminUserTableRow,
} from '../../../types/admin-users';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import { normalizeStatus } from '../../../constants/admin-users.constants';
import { CopyButton } from '../../ui/CopyButton';
import { AdminUserActionCell } from './components/AdminUserActionCell';

/** Status chip CSS class mapping using Equal DS finpro-tags classes */
const STATUS_CHIP_CLASSES: Record<string, string> = {
  ACTIVE: 'status-active',
  INACTIVE: 'status-paused',
  // DEACTIVE (auto-lockout) is the same "disabled" state as INACTIVE — style it identically.
  DEACTIVE: 'status-paused',
  LOCKED: 'status-rejected',
  PENDING_APPROVAL: 'status-pending',
};

/** Status display labels */
const STATUS_CHIP_LABELS: Record<string, string> = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  DEACTIVE: 'INACTIVE',
  LOCKED: 'LOCKED',
  PENDING_APPROVAL: 'PENDING',
};

/** Default column order matching Figma */
const DEFAULT_COLUMN_ORDER = [
  'name', 'empId', 'deptCode', 'mobile', 'email', 'roleName', 'status', 'actions',
  'roleStatus', 'createdAt', 'createdBy',
];

/** Columns hidden by default (toggle-able via manage columns) */
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  roleStatus: false,
  createdAt: false,
  createdBy: false,
};

export interface AdminUsersTableProps {
  rows: AdminUserTableRow[];
  isLoading: boolean;
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (row: AdminUserTableRow) => void;
  onToggleStatus: (row: AdminUserTableRow) => void;
  onResetPassword: (row: AdminUserTableRow) => void;
  onApprove: (row: AdminUserTableRow) => void;
  onReject: (row: AdminUserTableRow) => void;
  permissions: AdminUserPermissions;
  onCreateRequested?: () => void;
  searchForm: AdminUserSearchFormState;
  onSearchFieldChange: (field: AdminUserSearchField) => void;
  onSearchValueChange: (value: string) => void;
  onSearchSubmit: (value: string, field: AdminUserSearchField) => void;
  onSearchReset: () => void;
  onRowClick?: (row: AdminUserTableRow) => void;
  currentUserId?: string | null;
}

export const AdminUsersTable: React.FC<AdminUsersTableProps> = ({
  rows,
  isLoading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onToggleStatus,
  onResetPassword,
  onApprove,
  onReject,
  permissions,
  //onCreateRequested,
  searchForm,
  onSearchFieldChange,
  onSearchValueChange,
  onSearchSubmit,
  onSearchReset,
  onRowClick,
  currentUserId,
}) => {
  // Column management state
  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_COLUMN_ORDER);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(DEFAULT_COLUMN_VISIBILITY);

  const searchOptions: Array<{ value: AdminUserSearchField; label: string }> = useMemo(
    () => [
      { value: 'name', label: 'Name of the User' },
      { value: 'email', label: 'Email ID' },
      { value: 'mobile', label: 'Mobile' },
      { value: 'emp_id', label: 'Employee ID' },
      { value: 'dept_code', label: 'Department Code' },
    ],
    []
  );

  const handleSearch = useCallback(
    (query: string, domain?: string) => {
      const trimmed = query.trim();
      const nextField = (domain ?? searchForm.searchField) as AdminUserSearchField;

      onSearchFieldChange(nextField);
      onSearchValueChange(query);

      if (trimmed.length === 0) {
        onSearchReset();
      } else {
        onSearchSubmit(trimmed, nextField);
      }
    },
    [onSearchFieldChange, onSearchReset, onSearchSubmit, onSearchValueChange, searchForm.searchField]
  );

  // Create a lookup map to get original row with 'raw' property by id
  const rowsById = useMemo(() => {
    const map = new Map<string, AdminUserTableRow>();
    rows.forEach((row) => map.set(row.id, row));
    return map;
  }, [rows]);

  const columns = useMemo<TableColumn<AdminUserTableRow>[]>(() => {
    const baseColumns: TableColumn<AdminUserTableRow>[] = [
      {
        key: 'name',
        header: 'Employee Name',
        width: 180,
        accessor: (row) => row.name,
        render: (value, row) => {
          const isPendingApproval = row.status === 'PENDING_APPROVAL';
          return (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">{value || '--'}</span>
              {isPendingApproval && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-900 text-white">
                  New
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'empId',
        header: 'Employee ID',
        width: 120,
        accessor: (row) => row.empId,
        render: (value) => <span className="text-sm">{value || '--'}</span>,
      },
      {
        key: 'deptCode',
        header: 'Business Dept Code',
        width: 150,
        accessor: (row) => row.deptCode,
        render: (value) => <span className="text-sm">{value || '--'}</span>,
      },
      {
        key: 'mobile',
        header: 'Mobile',
        width: 140,
        accessor: (row) => row.mobile,
        render: (value) => (
          <div className="flex items-center gap-1.5">
            <span className="text-sm truncate flex-1 min-w-0" title={value as string}>
              {value || '--'}
            </span>
            {value && <CopyButton text={value as string} className="flex-shrink-0 text-gray-700 hover:text-gray-900" />}
          </div>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        width: 220,
        accessor: (row) => row.email,
        render: (value) => (
          <div className="flex items-center gap-1.5">
            <span
              className="text-sm font-mono truncate flex-1 min-w-0"
              title={value as string}
            >
              {value || '--'}
            </span>
            {value && <CopyButton text={value as string} className="flex-shrink-0 text-gray-700 hover:text-gray-900" />}
          </div>
        ),
      },
      {
        key: 'roleName',
        header: 'Role',
        width: 140,
        accessor: (row) => row.roleName,
        render: (value, row) => {
          const isPendingApproval = row.status === 'PENDING_APPROVAL';
          if (isPendingApproval) {
            return (
              <span className="text-sm text-text-secondary italic">Awaiting Approval...</span>
            );
          }
          return <span className="text-sm">{value || '--'}</span>;
        },
      },
      {
        key: 'status',
        header: 'Account Status',
        width: 160,
        locked: true,
        accessor: (row) => row.status,
        render: (value) => {
          // Normalize so lowercase 'locked' / mixed casing resolve to the right chip.
          const normalized = normalizeStatus(value as string);
          const chipClass = STATUS_CHIP_CLASSES[normalized] ?? 'status-expired';
          const label = STATUS_CHIP_LABELS[normalized] ?? (value as string) ?? '--';
          return (
            <Tag size="sm" className={chipClass}>
              {label}
            </Tag>
          );
        },
      },
      // Hidden by default columns
      {
        key: 'roleStatus',
        header: 'Admin',
        width: 100,
        accessor: (row) => row.roleStatus,
        render: (value) => (
          <span className={`text-sm font-medium ${value === 'Yes' ? 'text-green-600' : 'text-text-secondary'}`}>
            {value || '--'}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Timestamp',
        width: 180,
        accessor: (row) => row.createdAt,
        render: (value) => <span className="text-sm text-text-secondary">{value || '--'}</span>,
      },
      {
        key: 'createdBy',
        header: 'User Created By',
        width: 140,
        accessor: (row) => row.createdBy,
        render: (value) => <span className="text-sm">{value || '--'}</span>,
      },
    ];

    // Actions column rendered by AdminUserActionCell component
    const actionsColumn: TableColumn<AdminUserTableRow> = {
      key: 'actions',
      header: 'Actions',
      width: 280,
      align: 'center',
      isActions: true,
      locked: true,
      render: (_value, row) => {
        const originalRow = rowsById.get(row.id) ?? row;
        return (
          <AdminUserActionCell
            row={row}
            originalRow={originalRow}
            permissions={permissions}
            currentUserId={currentUserId}
            onToggleStatus={onToggleStatus}
            onEdit={onEdit}
            onResetPassword={onResetPassword}
            onApprove={onApprove}
            onReject={onReject}
          />
        );
      },
    };

    return [...baseColumns, actionsColumn];
  }, [permissions, currentUserId, onApprove, onEdit, onReject, onResetPassword, onToggleStatus, rowsById]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-sm bg-white">
      <div className="flex md:flex-row md:items-center md:justify-end gap-3 mb-4">
        <div className="w-full md:w-auto md:min-w-[320px]">
          <SearchBar
            variant="with-dropdown"
            domains={searchOptions}
            value={searchForm.searchValue}
            onChange={onSearchValueChange}
            selectedDomain={searchForm.searchField}
            onDomainChange={(domain) => onSearchFieldChange(domain as AdminUserSearchField)}
            onSearch={handleSearch}
            onClear={onSearchReset}
            placeholder="Search admin users"
            disabled={isLoading}
          />
        </div>
      </div>

      <Table
        className="flex-1 min-h-0"
        data={rows.map(({ raw, ...rest }) => rest) as any}
        columns={columns}
        loading={isLoading}
        pagination={{
          currentPage: pagination.page,
          pageSize: pagination.pageSize,
          totalItems: pagination.totalItems,
          onPageChange,
          onPageSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
        columnManager={{
          enabled: true,
          allowHiding: true,
          drawerTitle: 'Manage Columns',
        }}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        defaultColumnOrder={DEFAULT_COLUMN_ORDER}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
        onRowClick={onRowClick ? (row: any) => {
          // Resolve original row with raw data for the detail panel
          const originalRow = rowsById.get(row.id);
          if (originalRow && onRowClick) {
            onRowClick(originalRow);
          }
        } : undefined}
      />
    </div>
  );
};
